/**
 * Catalogue des agrégateurs — écran repris de la version du client (itérations 29, 31).
 *
 * Deux agrégateurs sont INTÉGRÉS, Symphonics et Survoltage : ils ont du code derrière
 * eux, un barème, un calcul et des écrans. L'écran les affiche sans permettre de les
 * modifier ni de les retirer — renommer « Survoltage » laisserait les routes, les
 * écrans et le barème contractuel continuer de s'appeler ainsi, et plus rien ne se
 * recouperait.
 *
 * Les autres sont des fiches, utiles pour préparer un raccordement à venir sans écrire
 * de code. Elles vivent dans le sous-compte : sa version les rangeait globalement, ce
 * qui les aurait fait apparaître chez tous les opérateurs.
 *
 * Seuls les chemins d'import changent par rapport à son fichier.
 */
import { useEffect, useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '@/shared/auth/AuthContext';
import useCapacites from '@/shared/auth/useCapacites';
import { CAP } from '@/shared/auth/capacites';
import api from '@/shared/api/client';
import Breadcrumb from '@/shared/components/Breadcrumb';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Badge } from '@/shared/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/shared/ui/dialog';
import { toast } from 'sonner';
import { ChevronRight, Clock, Plus, Trash2, CheckCircle2, Pencil } from 'lucide-react';
import ConfigurationAgregateur, {
  baremeVersLignes, lignesVersBareme, decotesVersLignes, lignesVersDecotes,
} from '@/features/aggregateurs/components/ConfigurationAgregateur';

const STATUS_MAP = {
  actif: { color: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle2, label: 'Actif' },
  en_attente: { color: 'bg-amber-50 text-amber-700 border-amber-200', icon: Clock, label: 'En attente' },
  bientot: { color: 'bg-neutral-100 text-neutral-600 border-neutral-200', icon: Clock, label: 'Bientôt' },
};

const ACCENT_MAP = {
  cyan: { text: 'text-cyan-700', border: 'border-cyan-100 hover:border-cyan-300', tint: 'from-cyan-50 via-white to-white' },
  violet: { text: 'text-violet-700', border: 'border-violet-100 hover:border-violet-300', tint: 'from-violet-50 via-white to-white' },
  orange: { text: 'text-orange-700', border: 'border-orange-100 hover:border-orange-300', tint: 'from-orange-50 via-white to-white' },
  blue: { text: 'text-blue-700', border: 'border-blue-100 hover:border-blue-300', tint: 'from-blue-50 via-white to-white' },
  emerald: { text: 'text-emerald-700', border: 'border-emerald-100 hover:border-emerald-300', tint: 'from-emerald-50 via-white to-white' },
  neutral: { text: 'text-neutral-700', border: 'border-neutral-200 hover:border-neutral-300', tint: 'from-neutral-50 via-white to-white' },
};

export default function AggregateursPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [aggregateurs, setAggregateurs] = useState([]);
  const [availableLogos, setAvailableLogos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = create, id = edit
  const [form, setForm] = useState({ name: '', tagline: '', logo_url: '', route: '', status: 'en_attente',
    status_label: 'Bientôt disponible', accent: 'neutral', prerequisites: '',
    types_actifs: [], parametres_calcul: {}, remuneration_lignes: [], decotes_lignes: [] });
  const [saving, setSaving] = useState(false);

  // La garde lit la CAPACITE, plus une liste de roles ecrite ici (lot I). Cette liste
  // etait une copie de la matrice du serveur : le 30/08, le gestionnaire a perdu
  // `tarifs:read` et la copie ne l'a pas su -- l'ecran s'ouvrait encore et restait vide.
  const { possede, pret } = useCapacites();
  const hasAccess = !pret || possede(CAP.TARIFS_READ);
  const isAdmin = user?.role === 'admin';

  const load = async () => {
    try {
      const r = await api.get('/aggregateurs');
      setAggregateurs(r.data.aggregateurs);
      setAvailableLogos(r.data.available_logos);
    } catch (e) { console.error(e); toast.error('Chargement impossible'); }
    setLoading(false);
  };

  // `hasAccess` se deduit de `user` : le declarer en dependance ne provoque aucun
  // rechargement supplementaire, et evite la mise en sourdine qui masquait l'avertissement.
  // Le commentaire d'origine etait pose sur la MEME ligne, donc il desactivait la
  // suivante — la regle continuait de crier, et le build restait rouge.
  useEffect(() => {
    if (!user) return;
    if (!hasAccess) { setLoading(false); return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, hasAccess]);

  if (user && !hasAccess) {
    const home = user.role === 'installateur' ? '/installateur' : user.role === 'distributeur' ? '/sav' : '/dashboard';
    return <Navigate to={home} replace />;
  }

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', tagline: '', logo_url: '', route: '', status: 'en_attente',
    status_label: 'Bientôt disponible', accent: 'neutral', prerequisites: '',
    types_actifs: [], parametres_calcul: {}, remuneration_lignes: [], decotes_lignes: [] });
    setDialogOpen(true);
  };

  const openEdit = (agg) => {
    setEditing(agg.id);
    setForm({
      name: agg.name || '',
      tagline: agg.tagline || '',
      logo_url: agg.logo_url || '',
      route: agg.route || '',
      status: agg.status || 'en_attente',
      status_label: agg.status_label || '',
      accent: agg.accent || 'neutral',
      prerequisites: Array.isArray(agg.prerequisites) ? agg.prerequisites.join('\n') : '',
      // F1 — les quatre familles de reglages du barème. Elles sont editees en LIGNES a
      // l ecran et stockees en OBJET cote API : un tableau se reordonne et se complete,
      // un objet ne se saisit pas.
      types_actifs: Array.isArray(agg.types_actifs) ? agg.types_actifs : [],
      parametres_calcul: agg.parametres_calcul || {},
      remuneration_lignes: baremeVersLignes(agg.remuneration_schedule),
      decotes_lignes: decotesVersLignes(agg.decotes),
    });
    setDialogOpen(true);
  };

  const handleClick = (agg) => {
    if (agg.status !== 'actif') return;
    // ROUTE PROPRE POUR LES INTEGRES, page generique pour les autres — 31/08.
    //
    // Avant, une fiche sans `route` etait « Indisponible » et ne s'ouvrait pas : TILT,
    // cree par David, restait une carte morte. Or Symphonics et Survoltage ont une
    // route parce qu'ils ont un ECRAN CODE A LA MAIN, pas parce qu'ils seraient plus
    // legitimes. Les autres en ont un aussi, desormais : le meme gabarit, sur leur
    // fiche.
    navigate(agg.route || `/agregateurs/${agg.key}`);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error('Nom requis');
    if (!form.logo_url) return toast.error('Logo requis');
    setSaving(true);
    try {
      const prereqList = (form.prerequisites || '').split('\n').map(s => s.trim()).filter(Boolean);
      const body = {
        ...form,
        route: form.route || null,
        prerequisites: prereqList,
        remuneration_schedule: lignesVersBareme(form.remuneration_lignes),
        decotes: lignesVersDecotes(form.decotes_lignes),
      };
      // Les deux tableaux de lignes sont une commodite d ECRAN. Les envoyer tels quels
      // ferait porter a la fiche deux representations du meme barème, qui divergeraient
      // au premier enregistrement fait ailleurs qu ici.
      delete body.remuneration_lignes;
      delete body.decotes_lignes;
      if (editing) {
        await api.put(`/aggregateurs/${editing}`, body);
        toast.success('Agrégateur mis à jour');
      } else {
        await api.post('/aggregateurs', body);
        toast.success('Agrégateur créé');
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
    setSaving(false);
  };

  const remove = async (agg) => {
    if (agg.is_builtin) return;
    if (!window.confirm(`Supprimer l'agrégateur "${agg.name}" ?`)) return;
    try {
      await api.delete(`/aggregateurs/${agg.id}`);
      toast.success('Supprimé');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  if (loading) return <div className="flex items-center justify-center h-full"><div className="w-6 h-6 border-2 border-neutral-300 border-t-neutral-900 rounded-full animate-spin" /></div>;

  return (
    <div className="p-6 md:p-8 lg:p-10 space-y-6" data-testid="aggregateurs-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <Breadcrumb items={[{ label: 'Agrégateur' }]} />
          <h1 className="text-2xl font-semibold tracking-tight text-neutral-900">Agrégateur</h1>
          <p className="text-sm text-neutral-400 mt-0.5">
            Sélectionnez un agrégateur pour consulter ses estimations, ou ajoutez-en un nouveau
          </p>
        </div>
        {isAdmin && (
          <Button onClick={openCreate} data-testid="btn-add-aggregateur">
            <Plus className="w-4 h-4 mr-1" /> Ajouter un agrégateur
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="aggregateurs-grid">
        {aggregateurs.map(agg => {
          const accent = ACCENT_MAP[agg.accent] || ACCENT_MAP.neutral;
          const st = STATUS_MAP[agg.status] || STATUS_MAP.en_attente;
          const StIcon = st.icon;
          const clickable = agg.status === 'actif';
          return (
            <div
              key={agg.id}
              className={`group relative overflow-hidden rounded-2xl border bg-gradient-to-br p-6 transition-all ${accent.tint} ${accent.border} ${clickable ? 'cursor-pointer hover:shadow-md' : ''}`}
              onClick={() => handleClick(agg)}
              data-testid={`aggregateur-card-${agg.key}`}
            >
              {isAdmin && !agg.is_builtin && (
                <div className="absolute top-3 right-3 flex gap-1">
                  <button onClick={(e) => { e.stopPropagation(); openEdit(agg); }} className="p-1.5 rounded-md bg-white/80 hover:bg-white text-neutral-500 hover:text-neutral-900 transition-colors" data-testid={`edit-${agg.key}`} aria-label="Modifier">
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); remove(agg); }} className="p-1.5 rounded-md bg-white/80 hover:bg-red-50 text-neutral-500 hover:text-red-500 transition-colors" data-testid={`delete-${agg.key}`} aria-label="Supprimer">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
              <div className="h-20 md:h-24 flex items-center justify-center mb-4">
                <img src={agg.logo_url} alt={`Logo ${agg.name}`} className="max-h-full max-w-full object-contain" data-testid={`aggregateur-logo-${agg.key}`} />
              </div>
              <div className="text-center space-y-1">
                <p className={`text-lg font-semibold ${accent.text}`}>{agg.name}</p>
                {agg.tagline && <p className="text-[12px] text-neutral-500 line-clamp-2">{agg.tagline}</p>}
              </div>
              <div className="mt-4 pt-3 border-t border-neutral-100 flex items-center justify-between">
                <Badge variant="outline" className={`text-[10px] flex items-center gap-1 ${st.color}`} data-testid={`status-${agg.key}`}>
                  <StIcon className="w-3 h-3" />
                  {agg.status === 'en_attente' ? (agg.status_label || 'En attente') : st.label}
                </Badge>
                {clickable ? (
                  <span className={`flex items-center gap-1 text-[12px] font-medium ${accent.text}`}>
                    Ouvrir <ChevronRight className="w-3.5 h-3.5" />
                  </span>
                ) : (
                  <span className="text-[11px] text-neutral-400">Indisponible</span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Dialog create/edit */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="aggregateur-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Modifier l'agrégateur" : 'Ajouter un agrégateur'}</DialogTitle>
            <DialogDescription>
              Renseignez le nom, la description et sélectionnez un logo parmi les préchargés.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-[11px] text-neutral-500">Nom *</Label>
              <Input className="h-9" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Ex. TotalEnergies" data-testid="agg-name" />
            </div>
            <div>
              <Label className="text-[11px] text-neutral-500">Description courte</Label>
              <Input className="h-9" value={form.tagline} onChange={e => setForm(f => ({ ...f, tagline: e.target.value }))} placeholder="Ex. Mécanisme d'effacement diffus" data-testid="agg-tagline" />
            </div>
            <div>
              <Label className="text-[11px] text-neutral-500">Logo *</Label>
              <div className="grid grid-cols-4 gap-2 mt-1 p-2 rounded-md border border-neutral-200 bg-neutral-50/40 max-h-[180px] overflow-y-auto" data-testid="agg-logo-picker">
                {availableLogos.map(l => (
                  <button
                    key={l.key} type="button"
                    onClick={() => setForm(f => ({ ...f, logo_url: l.url }))}
                    className={`h-14 border rounded-md bg-white flex items-center justify-center p-1 transition-colors ${form.logo_url === l.url ? 'border-cyan-500 ring-2 ring-cyan-100' : 'border-neutral-200 hover:border-neutral-400'}`}
                    data-testid={`agg-logo-choice-${l.key}`}
                    title={l.label}
                  >
                    <img src={l.url} alt={l.label} className="max-h-full max-w-full object-contain" />
                  </button>
                ))}
              </div>
              <div className="mt-2">
                <Label className="text-[10px] text-neutral-400">Ou URL personnalisée</Label>
                <Input className="h-8 text-[11px]" value={form.logo_url} onChange={e => setForm(f => ({ ...f, logo_url: e.target.value }))} placeholder="/logos/mon_logo.png ou https://..." data-testid="agg-logo-url" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] text-neutral-500">Statut</Label>
                <select className="h-9 w-full rounded-md border border-input bg-transparent px-2 text-[12px]" value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))} data-testid="agg-status">
                  <option value="en_attente">En attente</option>
                  <option value="actif">Actif</option>
                  <option value="bientot">Bientôt</option>
                </select>
              </div>
              <div>
                <Label className="text-[11px] text-neutral-500">Couleur d&apos;accent</Label>
                <select className="h-9 w-full rounded-md border border-input bg-transparent px-2 text-[12px]" value={form.accent} onChange={e => setForm(f => ({ ...f, accent: e.target.value }))} data-testid="agg-accent">
                  {['neutral', 'cyan', 'violet', 'orange', 'blue', 'emerald'].map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
            {form.status === 'en_attente' && (
              <div>
                <Label className="text-[11px] text-neutral-500">Libellé du statut</Label>
                <Input className="h-9" value={form.status_label} onChange={e => setForm(f => ({ ...f, status_label: e.target.value }))} placeholder="En attente de contrat / formules / etc." />
              </div>
            )}
            {form.status === 'actif' && (
              <div>
                <Label className="text-[11px] text-neutral-500">Route interne (optionnel)</Label>
                <Input className="h-9" value={form.route} onChange={e => setForm(f => ({ ...f, route: e.target.value }))} placeholder="/mon-module (laisser vide si non applicable)" data-testid="agg-route" />
              </div>
            )}
            <div>
              <Label className="text-[11px] text-neutral-500">Prérequis techniques (une ligne par prérequis)</Label>
              <textarea
                className="w-full min-h-[90px] rounded-md border border-input bg-transparent px-3 py-2 text-[12px]"
                value={form.prerequisites}
                onChange={e => setForm(f => ({ ...f, prerequisites: e.target.value }))}
                placeholder={"Ex.\nCompteur Linky obligatoire\nAPI constructeur\nContrat OA à ~0,10 €/kWh"}
                data-testid="agg-prerequisites"
              />
            </div>
            {/* La ligne qui vivait ici promettait que « le barème peut être configuré
                ensuite ». Il ne pouvait pas l'être : c'est le retour de David, item F1.
                La promesse est remplacée par le formulaire qu'elle annonçait. */}
            <ConfigurationAgregateur valeur={form} onChange={setForm} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
            <Button onClick={save} disabled={saving} data-testid="agg-save">
              {saving ? 'Enregistrement…' : (editing ? 'Mettre à jour' : 'Créer')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
